// Signing in. The email link is the way in that needs nothing remembered, so
// it leads; a password is there for anyone who set one, which in practice is
// the admin. The account is the same either way.
import { safeNext, type MagicLinkSent } from "@bibleunleashed/shared";
import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Redirect, useLocation, useSearchParams } from "wouter";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { post } from "@/lib/api";
import { useLogin, useRegister, useUser } from "@/lib/auth";

/** Why a sign-in link did not work, as /api/auth/magic/:token redirects it. */
const LINK_TROUBLE: Record<string, string> = {
  expired: "That link has expired — they last 20 minutes. Ask for a new one.",
  used: "That link has already been used. Ask for a new one.",
  invalid: "That link isn’t valid any more. Ask for a new one.",
};

export function Login() {
  const { data: user } = useUser();
  const [, navigate] = useLocation();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<"link" | "password" | "register">("link");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  const login = useLogin();
  const register = useRegister();
  const magic = useMutation({
    mutationFn: (body: { email: string; name?: string; next?: string }) => post<MagicLinkSent>("/api/auth/magic", body),
  });

  const pending = login.isPending || register.isPending || magic.isPending;
  const error = (mode === "password" ? login.error : mode === "register" ? register.error : magic.error)?.message;
  const trouble = LINK_TROUBLE[params.get("link") ?? ""];
  // Where signing in was interrupted from — the Claude consent page, say.
  const next = safeNext(params.get("next"));

  if (user) return <Redirect to={next ?? "/commentary"} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (mode === "link") {
        await magic.mutateAsync({ email, next });
        return;
      }
      if (mode === "password") await login.mutateAsync({ email, password });
      else await register.mutateAsync({ email, password, name });
      navigate(next ?? "/commentary");
    } catch {
      /* shown via error */
    }
  };

  const field =
    "mt-1 w-full rounded-md border border-rule bg-paper-2 px-3 py-2 text-[14px] text-ink focus:border-accent focus-visible:outline-none";
  const label = "text-[12px] tracking-[0.06em] text-ink-3 uppercase";
  const heading = mode === "register" ? "Create an account" : "Sign in";

  // Asked for and sent: nothing else on this page is useful now.
  if (magic.isSuccess) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="mx-auto max-w-sm px-6 py-16">
          <Icon name="mail" width="26" height="26" className="text-accent" />
          <h1 className="mt-3 font-serif text-[28px] font-semibold">Check your email</h1>
          <p className="mt-2 font-serif text-[16px] leading-relaxed text-ink-2">
            If <span className="text-ink">{email}</span> has an account — or is about to — a sign-in link is on its way.
            It works once, and expires in twenty minutes.
          </p>
          {magic.data?.devLink && (
            <p className="mt-5 rounded-md border border-dashed border-rule bg-paper-2 p-3 text-[13px] text-ink-2">
              No mail provider is configured on this server, so here is the link:{" "}
              <a href={magic.data.devLink} className="break-all text-accent">
                {magic.data.devLink}
              </a>
            </p>
          )}
          <button onClick={() => magic.reset()} className="mt-6 text-[13px] text-accent">
            Use a different address
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-sm px-6 py-16">
        <h1 className="font-serif text-[28px] font-semibold">{heading}</h1>
        <p className="mt-1 text-[13px] text-ink-2">Free. Keeps your highlights, notes and settings.</p>
        {trouble && (
          <p className="mt-4 rounded-md border border-rule bg-paper-2 px-3 py-2 text-[13px] text-ink-2">{trouble}</p>
        )}

        <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
          {mode === "register" && (
            <label className={label}>
              Name
              <input className={field} value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
            </label>
          )}
          <label className={label}>
            Email
            <input
              className={field}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>
          {mode !== "link" && (
            <label className={label}>
              Password
              <input
                className={field}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === "register" ? 10 : 1}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
              />
            </label>
          )}
          {error && <p className="text-[13px] text-accent">{error}</p>}
          <button
            disabled={pending}
            className="flex items-center justify-center gap-2 rounded-md bg-accent px-4 py-2 text-[14px] font-medium text-white disabled:opacity-60"
          >
            {mode === "link" && <Icon name="mail" width="15" height="15" />}
            {mode === "link" ? "Email me a sign-in link" : mode === "password" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="mt-5 flex flex-col items-start gap-2 text-[13px]">
          {mode !== "link" && (
            <button onClick={() => setMode("link")} className="text-accent">
              Sign in with an email link instead
            </button>
          )}
          {mode !== "password" && (
            <button onClick={() => setMode("password")} className="text-ink-2 hover:text-ink">
              Use a password
            </button>
          )}
          {mode !== "register" && (
            <button onClick={() => setMode("register")} className="text-ink-2 hover:text-ink">
              Create an account with a password
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
